import { useEffect, useRef } from 'react';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import type { Workspace } from '@workspace/domain/workspace';
import { ViewModelItem } from '../items';
import { useSidebarViewModel } from './useSidebarViewModel';
import { SidebarTreeViewItemContainer } from './SidebarTreeViewItemContainer';

type Props = {
  workspace: Workspace;
  viewModel: ViewModelEntity;
  shouldBeScrolled?: boolean;
  resetScrollViewModel?: () => void;
};

export const SidebarTreeViewModelItemContainer: React.FC<Props> = ({
  workspace,
  viewModel,
  shouldBeScrolled = false,
}: Props) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const { isOpen, handleToggleOpen, sortedViews, resetScrollViewModel, sidebarTreeContainerRef } = useSidebarViewModel(
    viewModel.id,
    viewModel.workspaceId
  );

  useEffect(() => {
    if (shouldBeScrolled && ref.current && sidebarTreeContainerRef?.current) {
      const rect = ref.current.getBoundingClientRect();
      const { top: treeContainerTop, bottom: treeContainerBottom } =
        sidebarTreeContainerRef.current.getBoundingClientRect();
      if (rect.top <= treeContainerTop || treeContainerBottom <= rect.bottom) {
        ref.current.scrollIntoView();
      }
      resetScrollViewModel();
    }
  }, [ref, shouldBeScrolled, resetScrollViewModel, sidebarTreeContainerRef, viewModel.id]);

  return (
    <ViewModelItem
      ref={ref}
      key={viewModel.id}
      viewModel={viewModel}
      workspace={workspace}
      isOpen={isOpen}
      toggleOpen={handleToggleOpen}
    >
      {sortedViews.map((view) => (
        <SidebarTreeViewItemContainer key={view.id} view={view} viewModel={viewModel} workspace={workspace} />
      ))}
    </ViewModelItem>
  );
};
